import * as React from 'react';
import { compose } from 'redux';

// HOC
import {
  withInjectReducer,
  WithInjectReducerProps,
} from '@shopee-user/platform';
import { connect, ConnectedProps } from 'react-redux';

// Redux
import {
  fetchAccountInfo,
  updateAccountInfo,
  REDUCER_NAME,
  reducer,
  getAccountInfo,
  getAccountAPIProgress,
  getAccountError,
  isOneCredentialLinked,
  isOneCredentialWhitelisted,
  State,
} from '@shopee-user/account-redux';

// Constants
import { AccountStatus } from './constants';
import { APIProgress, ErrorCode } from '@tob/shared/api-constants';

const mapStateToProps = (state: State) => {
  return {
    accountInfo: getAccountInfo(state),
    accountAPIProgress: getAccountAPIProgress(state),
    accountError: getAccountError(state),
    oneCredentialLinked: isOneCredentialLinked(state),
    oneCredentialWhitelisted: isOneCredentialWhitelisted(state),
  };
};

const actionCreators = {
  fetchAccountInfo,
  updateAccountInfo,
};

const connector = connect(mapStateToProps, actionCreators);

export type WithAccountInfoProps = ConnectedProps<typeof connector> &
  WithInjectReducerProps & {
    accountStatus: AccountStatus;
  };

export function withAccountInfo<P>() {
  return function (
    WrappedComponent: React.ComponentType<P & WithAccountInfoProps>
  ): React.ComponentType<P> {
    function ComponentWithAccountInfo(
      props: P & WithAccountInfoProps
    ): JSX.Element {
      const {
        injectAsyncReducer,
        fetchAccountInfo,
        accountInfo,
        accountAPIProgress,
        accountError,
      } = props;
      const accountStatus =
        accountAPIProgress === APIProgress.Init ||
        accountAPIProgress === APIProgress.Requested ||
        (accountAPIProgress === APIProgress.Failed &&
          accountError !== ErrorCode.NotLoggedIn)
          ? AccountStatus.UNKNOWN
          : accountAPIProgress === APIProgress.Success && !!accountInfo
          ? AccountStatus.LOGGED_IN
          : AccountStatus.NOT_LOGGED_IN;

      React.useEffect(() => {
        injectAsyncReducer({ [REDUCER_NAME]: reducer });
        void fetchAccountInfo({});
      }, [fetchAccountInfo, injectAsyncReducer]);

      return <WrappedComponent {...props} accountStatus={accountStatus} />;
    }

    return compose(
      connector,
      withInjectReducer
    )(ComponentWithAccountInfo) as React.ComponentType<P>;
  };
}
